import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import { ccApi } from '../../services/api';
import { useCcStore } from '../../store/ccStore';
import { useUnitStore } from '../../store/unitStore';
import type { ExerciseEvent } from '../../types/domain';

const TYPE_TONE: Record<string, string> = {
  SOS: 'text-red-400',
  PROXIMITY_200M: 'text-amber-400',
  EXECUTION_START: 'text-orange-400',
  EXECUTION_STOP: 'text-gray-300',
  ADJUDICATION: 'text-green-400',
  TARGET_ASSIGNED: 'text-cyan-300',
  TARGET_CREATED: 'text-cyan-300',
  COMMS_LOST: 'text-red-400',
  COMMS_RESTORED: 'text-green-400',
  PHASE_TAG: 'text-blue-300',
};

function csvEscape(v: unknown): string {
  let s = String(v ?? '');
  // Neutralize spreadsheet formula injection: a cell starting with = + - @ (or a
  // leading tab/CR) is prefixed with an apostrophe so Excel/Sheets treats it as text.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// date-fns `format` throws on an invalid Date; guard every timestamp render.
function safeTime(ts: unknown): string {
  const d = new Date(ts as string);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'HH:mm:ss');
}
function tsMillis(ts: unknown): number {
  const t = new Date(ts as string).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function FeedList({ compact = false }: { compact?: boolean }) {
  const { units } = useUnitStore();
  const live = useCcStore((s) => s.recentFeed);
  const [typeFilter, setTypeFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');

  const snapshotQ = useQuery({
    queryKey: ['feed', typeFilter, unitFilter],
    queryFn: async () =>
      (await ccApi.getFeed({ type: typeFilter || undefined, unitId: unitFilter || undefined, limit: 300 })).data
        .events as ExerciseEvent[],
    retry: false,
    refetchInterval: 15000,
  });

  // Merge live socket buffer with the fetched snapshot (dedupe by id, newest first).
  const events = useMemo(() => {
    const byId = new Map<string, ExerciseEvent>();
    [...(snapshotQ.data ?? []), ...live].forEach((e) => e?.id && byId.set(e.id, e));
    let list = Array.from(byId.values());
    if (typeFilter) list = list.filter((e) => e.type === typeFilter);
    if (unitFilter) list = list.filter((e) => e.unitId === unitFilter);
    return list.sort((a, b) => tsMillis(b.timestamp) - tsMillis(a.timestamp));
  }, [snapshotQ.data, live, typeFilter, unitFilter]);

  const knownTypes = useMemo(
    () => Array.from(new Set(events.map((e) => e.type))).sort(),
    [events]
  );

  const exportCsv = async () => {
    try {
      const res = await ccApi.exportFeedCsv({ type: typeFilter || undefined, unitId: unitFilter || undefined });
      downloadBlob(res.data as Blob, 'war-diary.csv');
    } catch {
      // Fallback: build CSV client-side, matching the server's exact column order (§19.1):
      // timestamp,type,severity,unitCode,callsign,target,summary
      const codeById = new Map(units.map((u) => [u.id, u.code]));
      const header = ['timestamp', 'type', 'severity', 'unitCode', 'callsign', 'target', 'summary'];
      const rows = events.map((e) =>
        [
          e.timestamp,
          e.type,
          e.severity,
          e.unitId ? codeById.get(e.unitId) ?? e.unitId : '',
          e.platoon?.callsign ?? '',
          e.target?.name ?? '',
          e.summary,
        ].map(csvEscape).join(',')
      );
      downloadBlob(new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv' }), 'war-diary.csv');
    }
  };

  return (
    <div className="rounded-xl border border-command-border bg-command-panel flex flex-col">
      <div className="flex items-center gap-2 p-3 border-b border-command-border flex-wrap">
        <h3 className="text-sm font-semibold text-gray-100 mr-auto">War Diary</h3>
        <select aria-label="Filter by type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
          <option value="">All types</option>
          {knownTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select aria-label="Filter by unit" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}
          className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
          <option value="">All units</option>
          {units.map((u) => <option key={u.id} value={u.id}>{u.code}</option>)}
        </select>
        <button type="button" onClick={exportCsv} className="px-2 py-1 rounded bg-command-accent/20 text-command-accent border border-command-accent/40 text-xs font-semibold">
          Export CSV
        </button>
      </div>
      <div className={clsx('overflow-auto', compact ? 'max-h-72' : 'max-h-[70vh]')}>
        <ul className="divide-y divide-command-border/50">
          {events.map((e) => (
            <li key={e.id} className="px-3 py-2 flex items-start gap-2">
              <span className="text-[10px] text-gray-500 font-tactical w-16 flex-shrink-0 mt-0.5">
                {safeTime(e.timestamp)}
              </span>
              <span className={clsx('text-[10px] font-bold w-28 flex-shrink-0', TYPE_TONE[e.type] ?? 'text-gray-400')}>
                {e.type}
              </span>
              <span className="text-xs text-gray-200 flex-1">{e.summary}</span>
            </li>
          ))}
          {events.length === 0 && <li className="px-3 py-8 text-center text-gray-500 text-sm">No events yet.</li>}
        </ul>
      </div>
    </div>
  );
}
